import { contrastRatio, FALLBACK_PALETTE, TEXT_MIN_CONTRAST, themeColours, type ReleaseThemeId } from './releaseThemes';

/** A kit palette has five colours. */
export const KIT_PALETTE_SIZE = 5;

const HEX_COLOUR = /^#[0-9a-fA-F]{6}$/;

/** True for a `#rrggbb` colour. */
export function isHexColour(value: unknown): value is string {
  return typeof value === 'string' && HEX_COLOUR.test(value);
}

/**
 * The palette override of a kit: exactly five `#rrggbb` colours. Returns them in lower case, or
 * null when the value is not a list of five valid colours. Pure.
 */
export function parsePaletteOverride(value: unknown): string[] | null {
  if (!Array.isArray(value) || value.length !== KIT_PALETTE_SIZE || !value.every(isHexColour)) {
    return null;
  }
  return value.map((colour) => colour.toLowerCase());
}

/**
 * The palette that the release theme builds its colours from: the override when it is valid, else
 * the colours of the cover. Pure.
 */
export function effectivePalette(
  coverPalette: readonly string[],
  override: readonly string[] | null | undefined
): string[] {
  return parsePaletteOverride(override) ?? [...coverPalette];
}

/** True when two palettes hold the same colours in the same order (case does not matter). */
export function samePalette(a: readonly string[] | null | undefined, b: readonly string[] | null | undefined): boolean {
  if (a === null || a === undefined || b === null || b === undefined) {
    return a === b;
  }
  return a.length === b.length && a.every((colour, index) => colour.toLowerCase() === b[index]?.toLowerCase());
}

/** The contrast that a theme keeps between its text and its background for a palette. */
export function themeTextContrast(themeId: ReleaseThemeId, palette: readonly string[]): number {
  const { text, background } = themeColours(themeId, palette);
  return contrastRatio(text, background);
}

/** True when a contrast ratio is below the 4.5:1 that text needs. */
export function isLowTextContrast(ratio: number): boolean {
  return ratio < TEXT_MIN_CONTRAST;
}

/**
 * True when the text of the theme is hard to read on its background for this palette (contrast
 * below 4.5:1). It is a warning only: the palette is still saved. Pure.
 */
export function hasLowTextContrast(themeId: ReleaseThemeId, palette: readonly string[]): boolean {
  return isLowTextContrast(themeTextContrast(themeId, palette));
}

/**
 * Five colours to start an edit from. A kit with five colours gives them; a kit with fewer (a song
 * with no readable cover) is filled up with the Music24 inks. Pure.
 */
export function paletteForEditing(palette: readonly string[]): string[] {
  const valid = palette.filter(isHexColour).map((colour) => colour.toLowerCase());
  const filled = [...valid];
  for (let index = 0; filled.length < KIT_PALETTE_SIZE; index += 1) {
    filled.push(FALLBACK_PALETTE[index % FALLBACK_PALETTE.length] as string);
  }
  return filled.slice(0, KIT_PALETTE_SIZE);
}
